import { append, concat, equals, find, includes, isNil } from "ramda";
import { Key } from "react";

import {
  IPublishScopeOrganization,
  IPublishScopeOrganizationNode,
} from "@/types/dept-tutorials";

export const getAllChildrenKeys = (
  nodes: IPublishScopeOrganizationNode[]
): Key[] => {
  const result = [];

  for (const node of nodes) {
    result.push(node.key);

    if (node.children) {
      const childrenResult = getAllChildrenKeys(node.children);

      if (childrenResult.length > 0) {
        result.push(...childrenResult);
      }
    }
  }

  return result;
};

export const getAllNotStaffChildrenKeys = (
  nodes: IPublishScopeOrganizationNode[]
): Key[] => {
  const result = [];

  for (const node of nodes) {
    if (node.type !== 0) {
      result.push(node.key);
    }

    if (node.children) {
      const childrenResult = getAllNotStaffChildrenKeys(node.children);

      if (childrenResult.length > 0) {
        result.push(...childrenResult);
      }
    }
  }

  return result;
};

/**
 * 根據 key 列表查找所有父節點（isLeaf 為 false 的節點）
 * @param keys - 要查找的 key 數組
 * @param nodes - 樹形節點數組
 * @returns 只包含 key 的父節點 key 數組
 *
 * @example
 * // 查找 key 為 ['1', '2', '3'] 中所有父節點
 * const parentKeys = findParentKeys(['1', '2', '3'], treeData);
 */
export const findParentKeys = (
  key: Key,
  nodes: IPublishScopeOrganizationNode[]
): Key[] => {
  const a = find((node) => {
    return equals(key, node.key) && !isNil(node.parentId);
  }, nodes);

  if (a && a.parentId) {
    return [a.parentId];
  }

  for (const node of nodes) {
    if (node.children) {
      const childrenResult = findParentKeys(key, node.children);

      if (childrenResult.length > 0) {
        const parentKeys = isNil(node.parentId)
          ? childrenResult
          : append(node.parentId, childrenResult);

        return parentKeys;
      }
    }
  }

  return [];
};

export const getExcludesNodes = (
  nodes: IPublishScopeOrganizationNode[],
  keys: Key[],
  key: Key
): IPublishScopeOrganizationNode[] => {
  const result = [];

  for (const node of nodes) {
    if (!includes(node.key, keys) && !equals(node.key, key)) {
      result.push(node);
    } else {
      if (!equals(node.key, key) && node.children) {
        const childrenResult = getExcludesNodes(node.children, keys, key);

        if (childrenResult.length > 0) {
          result.push(...childrenResult);
        }
      }
    }
  }

  return result;
};

export const searchInTree = (
  nodes: IPublishScopeOrganizationNode[],
  searchValue: string
): boolean => {
  const lowerSearchValue = searchValue.toLowerCase();

  for (const node of nodes) {
    const title = (node.title || "").toString().toLowerCase();

    if (title.includes(lowerSearchValue)) {
      return true;
    }

    if (node.children && node.children.length > 0) {
      if (searchInTree(node.children, searchValue)) {
        return true;
      }
    }
  }

  return false;
};

/**
 * 根據 key 列表查找所有父節點（isLeaf 為 false 的節點）
 * @param keys - 要查找的 key 數組
 * @param nodes - 樹形節點數組
 * @returns 只包含 key 的父節點 key 數組
 *
 * @example
 * // 查找 key 為 ['1', '2', '3'] 中所有父節點
 * const parentKeys = findParentKeys(['1', '2', '3'], treeData);
 */
export const findParentKeysByKeys = (
  keys: Key[],
  nodes: IPublishScopeOrganizationNode[]
): Key[] | boolean => {
  let result: Key[] | boolean = false;

  for (const node of nodes) {
    const isKeyIncluded = includes(node.key, keys);

    const previousResult: Key[] = Array.isArray(result) ? result : [];

    if (node.children) {
      const childrenResult = findParentKeysByKeys(keys, node.children);

      if (Array.isArray(childrenResult) && childrenResult.length > 0) {
        result = concat(previousResult, append(node.key, childrenResult));
      } else if (childrenResult) {
        result = append(node.key, previousResult);
      }
    }
    result = result ? result : isKeyIncluded;
  }

  return result;
};

/**
 * 根據 key 列表查找所有父節點（isLeaf 為 false 的節點）
 * @param keys - 要查找的 key 數組
 * @param nodes - 樹形節點數組
 * @returns 只包含 key 的父節點 key 數組
 *
 * @example
 * // 查找 key 為 ['1', '2', '3'] 中所有父節點
 * const parentKeys = findParentKeys(['1', '2', '3'], treeData);
 */
export const convertTree = (
  nodes: IPublishScopeOrganization[]
): IPublishScopeOrganizationNode[] => {
  return nodes.map((node) => {
    const children = node.children ? convertTree(node.children) : undefined;

    // 自身已授權，或存在已授權的後代節點時，該節點及其父節點不禁用
    const enabled =
      node.authorized || (children ?? []).some((child) => !child.disabled);

    const result: IPublishScopeOrganizationNode = {
      key: node.key,
      title: node.title,
      disabled: !enabled,
      checkable: node.authorized,
      parentId: node.parent_id,
      type: node.type,
      staffsCount: node.staffs_count,
    };

    if (children) {
      result.children = children;
    }

    return result;
  });
};

/**
 * 根據 key 查找樹形數據中對應的節點
 * @param key - 要查找的節點 key
 * @param tree - 樹形節點數組
 * @returns 對應節點，如果未找到返回 null
 */
export const getNodeByKey = (
  key: Key,
  tree: IPublishScopeOrganizationNode[]
): IPublishScopeOrganizationNode | null => {
  for (const node of tree) {
    if (node.key === key) {
      return node;
    }
    if (node.children) {
      const found = getNodeByKey(key, node.children);

      if (found) return found;
    }
  }

  return null;
};
